import { useState } from 'react';
import Image from 'next/image';
import { Loading } from 'components/Loading';
import { CLASS_TRACKING_GTM, DEFAULT_IMAGE } from 'constants/index';
import * as El from './CollectionCard.css';

type ImageUrls = {
  icon?: string;
  normal?: string;
  banner?: string;
  iconActive?: string;
  darkNormal?: string;
  web?: string;
};

interface CollectionCardProps {
  isLoading?: boolean;
  images: ImageUrls;
  label: string;
  fullSize?: boolean;
  href?: string;
  collection_id?: string;
  sectionPosition?: string;
  dataCreative?: string;
  loadingImage?: 'lazy' | 'eager';
  isCollectionSearchVariant?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

function CollectionCard({
  isLoading = false,
  images,
  label = '',
  fullSize = false,
  href = '#',
  collection_id,
  sectionPosition,
  dataCreative,
  loadingImage = 'eager',
  isCollectionSearchVariant = false,
  onClick
}: CollectionCardProps) {
  const [hasClicked, setHasClicked] = useState(false);

  const getVariantClass = (
    baseVariant: 'base',
    fullSizeVariant: 'fullSize',
    searchVariant: 'searchVariant'
  ): 'base' | 'fullSize' | 'searchVariant' => {
    if (isCollectionSearchVariant) return searchVariant;
    if (fullSize) return fullSizeVariant;
    return baseVariant;
  };

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    setHasClicked(true);
    onClick?.(event);
  };

  return (
    <a
      id="collection-card"
      className={`${CLASS_TRACKING_GTM.COLLECTION_CARD} ${El.CardContainer[isCollectionSearchVariant ? 'search' : 'base']}`}
      href={href}
      title={label}
      aria-label={label}
      onClick={handleClick}
      data-bannerid={`${collection_id}`}
      data-name={`${label}`}
      data-position={`${sectionPosition || ''}`}
      data-creative={dataCreative}
      target="_top"
      style={{ filter: isLoading ? 'none' : '' }}
    >
      <div
        className={
          El.ImageContainer[
            getVariantClass('base', 'fullSize', 'searchVariant')
          ]
        }
      >
        <div
          className={
            El.ContentWrapper[
              getVariantClass('base', 'fullSize', 'searchVariant')
            ]
          }
        >
          {hasClicked && (
            <div className={El.CollectionCardLoading}>
              <Loading />
            </div>
          )}
          <Image
            src={images.icon || images.banner || DEFAULT_IMAGE}
            width={46}
            height={46}
            className={El.IconStyle}
            alt={label}
            loading={loadingImage}
            priority={loadingImage === 'eager'}
          />
          <div
            className={
              El.CollectionName[
                getVariantClass('base', 'fullSize', 'searchVariant')
              ]
            }
          >
            <h3 className={El.CollectionCardLabel}>{label}</h3>
          </div>
        </div>
      </div>
    </a>
  );
}

export default CollectionCard;
